import { BUDGET_OPTIONS, CURRENCIES, DISCOUNTABLE_LINES } from './types.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const BUDGET_KEY = /^[a-z0-9_-]{1,20}$/;
const TYPE_KEY = /^[a-z0-9_]{1,40}$/;
class Collector {
    errors = [];
    add(path, code, message) { this.errors.push({ path, code, message }); }
    int(path, v, min, max) {
        if (!isNum(v))
            return this.add(path, 'type', 'must be a number');
        if (!Number.isInteger(v))
            return this.add(path, 'integer', 'must be a whole number');
        if (v < min || v > max)
            this.add(path, 'range', `must be between ${min} and ${max}`);
    }
    num(path, v, min, max) {
        if (!isNum(v))
            return this.add(path, 'type', 'must be a number');
        if (v < min || v > max)
            this.add(path, 'range', `must be between ${min} and ${max}`);
    }
    optPct(path, v) {
        if (v === undefined || v === null)
            return;
        this.num(path, v, 0, 100);
    }
    str(path, v, max, required = true) {
        if (v === undefined || v === null || v === '') {
            if (required)
                this.add(path, 'required', 'is required');
            return;
        }
        if (typeof v !== 'string')
            return this.add(path, 'type', 'must be text');
        if (v.length > max)
            this.add(path, 'range', `must be at most ${max} characters`);
    }
    bool(path, v) {
        if (typeof v !== 'boolean')
            this.add(path, 'type', 'must be true or false');
    }
}
export function validateEstimateInputs(raw, book) {
    const c = new Collector();
    if (!isObj(raw)) {
        c.add('', 'type', 'inputs must be an object');
        return c.errors;
    }
    if (raw.offerType !== book.offerType)
        c.add('offerType', 'enum', `must be '${book.offerType}'`);
    if (!CURRENCIES.includes(raw.currency))
        c.add('currency', 'enum', `must be one of ${CURRENCIES.join(', ')}`);
    else if (raw.currency !== 'CAD')
        c.num('fxRate', raw.fxRate, 0.0001, 1000);
    const types = new Map(book.rates.equipment.map((t) => [t.key, t]));
    if (!Array.isArray(raw.equipment))
        c.add('equipment', 'type', 'must be a list');
    else if (raw.equipment.length > 100)
        c.add('equipment', 'range', 'at most 100 lines');
    else {
        const seen = new Set();
        raw.equipment.forEach((item, i) => {
            const p = `equipment[${i}]`;
            if (!isObj(item))
                return c.add(p, 'type', 'must be an object');
            const type = typeof item.typeKey === 'string' ? types.get(item.typeKey) : undefined;
            if (!type)
                c.add(`${p}.typeKey`, 'unknown', 'unknown equipment type for this price book');
            else if (seen.has(type.key))
                c.add(`${p}.typeKey`, 'duplicate', 'equipment type already listed');
            else
                seen.add(type.key);
            c.int(`${p}.qty`, item.qty, 0, 100000);
            if (typeof item.calibrated !== 'boolean')
                c.add(`${p}.calibrated`, 'type', 'must be true or false');
            else if (item.calibrated && type && !type.calibratable)
                c.add(`${p}.calibrated`, 'not_calibratable', `${type.label.en} cannot be calibrated`);
            c.optPct(`${p}.discountPct`, item.discountPct);
        });
    }
    c.int('thirdPartySensors', raw.thirdPartySensors, 0, 100000);
    const conn = raw.connectivity;
    if (!isObj(conn) || (conn.mode !== 'wifi' && conn.mode !== 'gateway'))
        c.add('connectivity.mode', 'enum', "must be 'wifi' or 'gateway'");
    else if (conn.mode === 'gateway') {
        c.int('connectivity.gateways', conn.gateways, 0, 1000);
        c.int('connectivity.dataPlans', conn.dataPlans, 0, 1000);
    }
    if (raw.km !== null)
        c.num('km', raw.km, 0, 20000);
    c.int('calibrationVisitsPerYear', raw.calibrationVisitsPerYear, 0, 365);
    c.int('calibrationShipmentsPerYear', raw.calibrationShipmentsPerYear, 0, 365);
    c.num('contingencyPct', raw.contingencyPct, 0, 100);
    if (!isObj(raw.lineDiscounts))
        c.add('lineDiscounts', 'type', 'must be an object');
    else
        for (const k of DISCOUNTABLE_LINES)
            c.optPct(`lineDiscounts.${k}`, raw.lineDiscounts[k]);
    if (!Array.isArray(raw.budgets) || raw.budgets.length < 1 || raw.budgets.length > 8)
        c.add('budgets', 'range', 'between 1 and 8 budgets');
    else {
        const keys = new Set();
        raw.budgets.forEach((b, i) => {
            const p = `budgets[${i}]`;
            if (!isObj(b))
                return c.add(p, 'type', 'must be an object');
            if (typeof b.key !== 'string' || !BUDGET_KEY.test(b.key))
                c.add(`${p}.key`, 'format', 'lowercase letters, digits, - or _ (max 20)');
            else if (keys.has(b.key))
                c.add(`${p}.key`, 'duplicate', 'budget key already used');
            else
                keys.add(b.key);
            c.str(`${p}.label`, b.label, 80);
            c.str(`${p}.subtitle`, b.subtitle, 300, false);
            if (!isObj(b.options))
                c.add(`${p}.options`, 'type', 'must be an object');
            else
                for (const o of BUDGET_OPTIONS)
                    c.bool(`${p}.options.${o}`, b.options[o]);
            c.num(`${p}.discountPct`, b.discountPct, 0, 100);
        });
    }
    return c.errors;
}
function validateSkuTiers(c, path, v) {
    if (!Array.isArray(v) || v.length === 0)
        return c.add(path, 'required', 'at least one SKU tier is required');
    v.forEach((t, i) => {
        if (!isObj(t))
            return c.add(`${path}[${i}]`, 'type', 'must be an object');
        c.int(`${path}[${i}].minQty`, t.minQty, 0, 1_000_000);
        c.str(`${path}[${i}].sku`, t.sku, 50);
    });
}
function validatePriced(c, path, v) {
    if (!isObj(v))
        return c.add(path, 'required', 'is required');
    c.str(`${path}.sku`, v.sku, 50);
    c.num(`${path}.price`, v.price, 0, 1_000_000);
}
function validateTravel(c, path, v) {
    if (!isObj(v))
        return c.add(path, 'required', 'is required');
    c.str(`${path}.sku`, v.sku, 50);
    c.num(`${path}.perKm`, v.perKm, 0, 100);
    c.bool(`${path}.roundTrip`, v.roundTrip);
    c.num(`${path}.roundDownTo`, v.roundDownTo, 1, 10_000);
    c.num(`${path}.flatBelowKm`, v.flatBelowKm, 0, 10_000);
    c.num(`${path}.flatFee`, v.flatFee, 0, 100_000);
    c.num(`${path}.max`, v.max, 0, 1_000_000);
}
function validateEquipmentTypes(c, raw) {
    if (!Array.isArray(raw) || raw.length === 0 || raw.length > 100)
        return c.add('equipment', 'range', 'between 1 and 100 equipment types');
    const keys = new Set();
    raw.forEach((t, i) => {
        const p = `equipment[${i}]`;
        if (!isObj(t))
            return c.add(p, 'type', 'must be an object');
        if (typeof t.key !== 'string' || !TYPE_KEY.test(t.key))
            c.add(`${p}.key`, 'format', 'lowercase letters, digits or _ (max 40)');
        else if (keys.has(t.key))
            c.add(`${p}.key`, 'duplicate', 'equipment key already used');
        else
            keys.add(t.key);
        if (!isObj(t.label))
            c.add(`${p}.label`, 'required', 'is required');
        else {
            c.str(`${p}.label.en`, t.label.en, 80);
            c.str(`${p}.label.fr`, t.label.fr, 80);
        }
        c.str(`${p}.sku`, t.sku, 50);
        c.num(`${p}.unitPrice`, t.unitPrice, 0, 1_000_000);
        c.int(`${p}.sensorsPerTransmitter`, t.sensorsPerTransmitter, 1, 20);
        c.bool(`${p}.calibratable`, t.calibratable);
    });
}
export function validateMonitoringRates(raw) {
    const c = new Collector();
    if (!isObj(raw)) {
        c.add('', 'type', 'rates must be an object');
        return c.errors;
    }
    if (raw.currency !== 'CAD')
        c.add('currency', 'enum', "must be 'CAD'");
    if (!isObj(raw.origin))
        c.add('origin.address', 'required', 'is required');
    else
        c.str('origin.address', raw.origin.address, 300);
    validateEquipmentTypes(c, raw.equipment);
    validatePriced(c, 'gateway', raw.gateway);
    if (!isObj(raw.dataPlan))
        c.add('dataPlan', 'required', 'is required');
    else {
        c.num('dataPlan.yearlyPrice', raw.dataPlan.yearlyPrice, 0, 1_000_000);
        validateSkuTiers(c, 'dataPlan.skuTiers', raw.dataPlan.skuTiers);
    }
    const services = isObj(raw.services) ? raw.services : {};
    for (const k of ['installationPerDevice', 'configurationPerSensor', 'iqoqPerSensor'])
        validatePriced(c, `services.${k}`, services[k]);
    const recurring = isObj(raw.recurring) ? raw.recurring : {};
    for (const k of ['hosting', 'support', 'warranty', 'calibration']) {
        const r = recurring[k];
        const p = `recurring.${k}`;
        if (!isObj(r)) {
            c.add(p, 'required', 'is required');
            continue;
        }
        c.num(`${p}.baseMonthly`, r.baseMonthly, 0, 10_000);
        c.int(`${p}.stepEvery`, r.stepEvery, 0, 100_000);
        c.num(`${p}.stepMonthly`, r.stepMonthly, 0, 10_000);
        c.int(`${p}.maxSteps`, r.maxSteps, 0, 1000);
        validateSkuTiers(c, `${p}.skuTiers`, r.skuTiers);
    }
    validateTravel(c, 'travel', raw.travel);
    validateTravel(c, 'calibrationTravel', raw.calibrationTravel);
    if (!isObj(raw.shipping))
        c.add('shipping', 'required', 'is required');
    else {
        c.str('shipping.sku', raw.shipping.sku, 50);
        c.num('shipping.base', raw.shipping.base, 0, 100_000);
        c.int('shipping.devicesPerBase', raw.shipping.devicesPerBase, 1, 10_000);
        c.num('shipping.kmPerMultiplier', raw.shipping.kmPerMultiplier, 1, 100_000);
        c.num('shipping.calibrationPerShipment', raw.shipping.calibrationPerShipment, 0, 100_000);
    }
    const limits = isObj(raw.discountLimits) ? raw.discountLimits : {};
    for (const k of ['hardware', 'services', 'budget'])
        c.num(`discountLimits.${k}`, limits[k], 0, 100);
    const tiers = isObj(raw.volumeTiers) ? raw.volumeTiers : {};
    for (const k of ['hardware', 'services']) {
        const t = tiers[k];
        const p = `volumeTiers.${k}`;
        if (t === null)
            continue;
        if (!isObj(t)) {
            c.add(p, 'type', 'must be null or a tier');
            continue;
        }
        c.num(`${p}.pctPerStep`, t.pctPerStep, 0, 100);
        c.int(`${p}.stepEvery`, t.stepEvery, 1, 100_000);
        c.num(`${p}.maxPct`, t.maxPct, 0, 100);
        const limit = limits[k];
        if (isNum(t.maxPct) && isNum(limit) && t.maxPct > limit)
            c.add(`${p}.maxPct`, 'exceeds_limit', `cannot exceed discountLimits.${k}`);
    }
    c.num('fxMarkupPct', raw.fxMarkupPct, 0, 100);
    return c.errors;
}
export function collectRateSkus(rates) {
    const skus = new Set();
    rates.equipment.forEach((t) => skus.add(t.sku));
    skus.add(rates.gateway.sku);
    rates.dataPlan.skuTiers.forEach((t) => skus.add(t.sku));
    Object.values(rates.services).forEach((s) => skus.add(s.sku));
    Object.values(rates.recurring).forEach((r) => r.skuTiers.forEach((t) => skus.add(t.sku)));
    [rates.travel.sku, rates.calibrationTravel.sku, rates.shipping.sku].forEach((s) => skus.add(s));
    return [...skus].filter(Boolean).sort();
}
/**
 * Rebuilds validated inputs with only the known fields, so nothing unexpected is stored.
 * Call after validateEstimateInputs has returned no errors.
 */
export function normalizeEstimateInputs(inputs) {
    const connectivity = inputs.connectivity.mode === 'gateway'
        ? { mode: 'gateway', gateways: inputs.connectivity.gateways, dataPlans: inputs.connectivity.dataPlans }
        : { mode: 'wifi' };
    const options = (o) => Object.fromEntries(BUDGET_OPTIONS.map((k) => [k, o[k]]));
    return {
        offerType: inputs.offerType,
        currency: inputs.currency,
        fxRate: inputs.fxRate,
        equipment: inputs.equipment.map((e) => ({ typeKey: e.typeKey, qty: e.qty, calibrated: e.calibrated, discountPct: e.discountPct ?? null })),
        thirdPartySensors: inputs.thirdPartySensors,
        connectivity,
        km: inputs.km,
        calibrationVisitsPerYear: inputs.calibrationVisitsPerYear,
        calibrationShipmentsPerYear: inputs.calibrationShipmentsPerYear,
        contingencyPct: inputs.contingencyPct,
        lineDiscounts: Object.fromEntries(DISCOUNTABLE_LINES.map((k) => [k, inputs.lineDiscounts[k] ?? null])),
        budgets: inputs.budgets.map((b) => ({
            key: b.key, label: b.label, subtitle: b.subtitle ?? null, options: options(b.options), discountPct: b.discountPct,
        })),
    };
}
//# sourceMappingURL=validate.js.map